import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Copy, ExternalLink, Link2, Loader2, QrCode, Store, User, Wallet } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import Reveal, { Eyebrow } from "@/components/Reveal";
import PayFlow, { CopyRow, Row, fmtUnits, usePaymentStatus } from "@/components/PayFlow";
import { useWallet } from "@/lib/wallet";
import { toBaseUnits } from "@/lib/pay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-lg border border-line bg-panel px-4 py-3 font-mono text-sm text-white outline-none transition-colors focus:border-sgreen/60";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mut";

function StatusChip({ status }) {
  const map = {
    created: ["Awaiting payment", "text-white border-line bg-white/[0.04]"],
    submitted: ["Confirming…", "text-sgreen border-sgreen/30 bg-sgreen/10"],
    confirmed: ["Settled", "text-sgreen border-sgreen/40 bg-sgreen/10"],
    failed: ["Failed", "text-red-400 border-red-400/30 bg-red-400/10"],
  };
  const [label, cls] = map[status] || map.created;
  return (
    <span data-testid="payment-status-chip" className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${cls}`}>
      {(status === "created") && <span className="ping-dot h-1.5 w-1.5 rounded-full bg-sgreen" />}
      {label}
    </span>
  );
}

function MerchantTab() {
  const { address } = useWallet();
  const [wallet, setWallet] = useState("");
  const [amount, setAmount] = useState("49.00");
  const [memo, setMemo] = useState("Order #1042");
  const [payment, setPayment] = useState(null);
  const [creating, setCreating] = useState(false);
  const [qr, setQr] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (address && !wallet) setWallet(address);
  }, [address]);

  const loadStats = async (w) => {
    try {
      const r = await fetch(`${API}/payments?merchant_wallet=${w}`);
      if (r.ok) setStats((await r.json()).stats);
    } catch (e) {}
  };

  useEffect(() => {
    if (wallet.length >= 32) loadStats(wallet);
  }, [wallet]);

  const { payment: livePayment } = usePaymentStatus(payment?.id, () => {
    toast.success("Payment confirmed on-chain");
    if (wallet.length >= 32) loadStats(wallet);
  });
  useEffect(() => {
    if (livePayment && livePayment.status !== payment?.status) setPayment(livePayment);
  }, [livePayment?.status]);

  const create = async () => {
    let units;
    try {
      units = toBaseUnits(amount);
    } catch (e) {
      toast.error(e.message);
      return;
    }
    if (wallet.length < 32) {
      toast.error("Enter a valid Solana wallet address");
      return;
    }
    setCreating(true);
    try {
      const r = await fetch(`${API}/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ merchant_wallet: wallet.trim(), amount, memo, label: "B&W Pay demo" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not create payment");
      setPayment(d);
      QRCode.toDataURL(d.link, { margin: 1, width: 256, color: { dark: "#0a0a0a", light: "#ffffff" } }).then(setQr);
    } catch (e) {
      toast.error(e?.message || "Could not create payment");
    }
    setCreating(false);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(payment.link).then(
      () => toast.success("Payment link copied"),
      () => toast.error("Copy failed")
    );
  };

  return (
    <div data-testid="merchant-tab">
      {stats && (
        <div className="mb-6 grid grid-cols-3 gap-3" data-testid="merchant-stats">
          {[
            ["Settled volume", `${stats.total_volume.toLocaleString("en-US", { maximumFractionDigits: 2 })} USDC`],
            ["Payments", `${stats.settled}${stats.pending ? ` · ${stats.pending} pending` : ""}`],
            ["Fees (1%)", `${stats.fees.toLocaleString("en-US", { maximumFractionDigits: 4 })} USDC`],
          ].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-line bg-panel p-4" data-testid={`merchant-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="font-mono text-sm font-semibold text-white sm:text-base">{v}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{k}</p>
            </div>
          ))}
        </div>
      )}

      {!payment ? (
        <div className="grid gap-5 sm:grid-cols-2" data-testid="merchant-form">
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="merchant-wallet-input">Merchant wallet (receives 99%)</label>
            <div className="flex gap-2">
              <input
                id="merchant-wallet-input"
                data-testid="merchant-wallet-input"
                type="text"
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                className={inputCls}
                placeholder="Your Solana wallet address"
              />
              {address && wallet !== address && (
                <button
                  onClick={() => setWallet(address)}
                  data-testid="merchant-use-connected"
                  className="shrink-0 rounded-lg border border-sgreen/40 bg-sgreen/10 px-4 text-xs font-medium text-sgreen"
                >
                  Use connected
                </button>
              )}
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="merchant-amount-input">Amount (USDC)</label>
            <input
              id="merchant-amount-input"
              data-testid="merchant-amount-input"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputCls}
              placeholder="49.00"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="merchant-memo-input">Memo / Order ID</label>
            <input
              id="merchant-memo-input"
              data-testid="merchant-memo-input"
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className={inputCls}
              placeholder="Order #1042"
            />
          </div>
          <button
            onClick={create}
            disabled={creating}
            data-testid="merchant-create-button"
            className="cta-glow sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-3.5 text-sm font-semibold text-black disabled:opacity-50"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />}
            {creating ? "Creating…" : "Create Payment Request"}
          </button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="merchant-payment-view">
          <div className="flex flex-col items-start gap-7 sm:flex-row sm:items-center">
            {qr ? (
              <img src={qr} alt="Payment QR code" data-testid="gateway-qr" className="w-36 shrink-0 rounded-xl bg-white p-2" />
            ) : (
              <div className="flex h-36 w-36 shrink-0 items-center justify-center rounded-xl border border-line">
                <QrCode className="h-8 w-8 text-mut" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-mono text-3xl font-semibold text-white">
                  {fmtUnits(payment.amount_units)}
                  <span className="ml-2 text-sm font-medium text-sgreen">USDC</span>
                </p>
                <StatusChip status={payment.status} />
              </div>
              <p className="mt-1.5 font-mono text-xs text-mut">
                To: {payment.merchant_wallet.slice(0, 8)}…{payment.merchant_wallet.slice(-4)} · {payment.memo || "No memo"}
              </p>
              <div className="mt-4">
                <CopyRow text={payment.link} label="Payment link" />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Row k="Merchant receives (99%)" v={`${fmtUnits(payment.merchant_units)} USDC`} />
            <Row k="Platform fee (1%)" v={`${fmtUnits(payment.fee_units)} USDC`} green />
            {payment.status === "confirmed" && (
              <>
                <Row k="Settled" v={new Date(payment.confirmed_at).toLocaleTimeString()} green />
                <a
                  href={`https://solscan.io/tx/${payment.signature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="merchant-explorer-link"
                  className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs text-sgreen"
                >
                  View transaction on Solscan <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
          </div>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button
              onClick={copyLink}
              data-testid="merchant-share-button"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-line px-6 py-3 text-sm text-white transition-colors hover:border-white/30"
            >
              <Link2 className="h-4 w-4 text-sgreen" />
              Copy link
            </button>
            <button
              onClick={() => setPayment(null)}
              data-testid="merchant-new-payment-button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-line px-6 py-3 text-sm text-white transition-colors hover:border-white/30"
            >
              <ArrowLeft className="h-4 w-4" />
              New payment
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function CustomerTab() {
  const [id, setId] = useState("");
  const [activeId, setActiveId] = useState("");

  const load = () => {
    const raw = id.trim();
    const m = raw.match(/pay\/([0-9a-fA-F]{24})/);
    const target = m ? m[1] : raw;
    if (!/^[0-9a-fA-F]{24}$/.test(target)) {
      toast.error("Paste a payment link or payment ID");
      return;
    }
    setActiveId(target);
  };

  return (
    <div data-testid="customer-tab">
      {!activeId ? (
        <div className="mx-auto max-w-lg" data-testid="customer-load-form">
          <label className={labelCls} htmlFor="customer-link-input">Payment link or ID</label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              id="customer-link-input"
              data-testid="customer-link-input"
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              className={inputCls}
              placeholder="https://…/pay/68fa…  or  68fa…"
            />
            <button
              onClick={load}
              data-testid="customer-load-button"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-3 text-sm font-semibold text-black"
            >
              <User className="h-4 w-4" />
              Open
            </button>
          </div>
          <p className="mt-4 font-mono text-xs leading-relaxed text-mut">
            Create a request in the Merchant tab, copy its link, then open it here — or on your phone — to pay from any Solana wallet.
          </p>
        </div>
      ) : (
        <div className="mx-auto max-w-lg rounded-2xl border border-line bg-panel p-6 sm:p-8">
          <PayFlow paymentId={activeId} />
        </div>
      )}
    </div>
  );
}

export default function GatewayConsole() {
  const [tab, setTab] = useState("merchant");

  return (
    <div
      className="overflow-hidden rounded-2xl border border-line bg-panel shadow-[0_40px_120px_-50px_rgba(20,241,149,0.18)]"
      data-testid="demo-console"
    >
      <div className="flex items-center justify-between border-b border-line bg-card2/60 px-5 py-3.5">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#2a2a2a]" />
          <span className="h-3 w-3 rounded-full bg-[#2a2a2a]" />
          <span className="h-3 w-3 rounded-full bg-sgreen/70" />
        </div>
        <span className="font-mono text-xs text-mut">bwpay console · mainnet</span>
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-sgreen">
          <span className="ping-dot h-1.5 w-1.5 rounded-full bg-sgreen" />
          Live
        </span>
      </div>

      <div className="flex gap-2 border-b border-line px-6 pt-5 sm:px-8">
        {[
          ["merchant", "Merchant", <Store className="h-3.5 w-3.5" key="s" />],
          ["customer", "Pay a request", <Wallet className="h-3.5 w-3.5" key="w" />],
        ].map(([id, label, icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`gateway-tab-${id}`}
            className={`inline-flex items-center gap-2 rounded-t-lg border-b-2 px-4 pb-3 pt-1 text-sm transition-colors ${
              tab === id ? "border-sgreen text-white" : "border-transparent text-mut hover:text-white"
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      <div className="p-6 sm:p-8">
        {tab === "merchant" ? <MerchantTab /> : <CustomerTab />}
      </div>
    </div>
  );
}

export function GatewaySection() {
  return (
    <section
      id="demo"
      className="relative scroll-mt-24 border-t border-line py-24 sm:py-32"
      data-testid="demo-section"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-sgreen/[0.05] blur-[120px]" />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-12">
        <Reveal className="text-center">
          <Eyebrow>Live demo · real payments</Eyebrow>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Try the console below.
          </h2>
          <p className="mt-4 text-base text-mut sm:text-lg">
            Real USDC on Solana mainnet — create a request, share the link, get paid. A 1%
            platform fee routes on-chain with every payment.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <GatewayConsole />
        </Reveal>
      </div>
    </section>
  );
}
